import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { BaseService } from '@devkitify/angular-ui-kit';
import { Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ThemeService } from '../theme';
import {
	ChangePasswordData,
	ChangePasswordRequest,
	CurrentUser,
	LoginData,
	LoginRequest,
} from './auth.model';
import { AuthStore } from './auth.store';

const ACCESS_TOKEN_KEY = 'saas_travel_access_token';
const REFRESH_TOKEN_KEY = 'saas_travel_refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
	private readonly baseService = inject(BaseService);
	private readonly authStore = inject(AuthStore);
	private readonly router = inject(Router);
	private readonly theme = inject(ThemeService);
	private readonly platformId = inject(PLATFORM_ID);

	private get isBrowser(): boolean {
		return isPlatformBrowser(this.platformId);
	}

	getAccessToken(): string | null {
		if (!this.isBrowser) return null;
		return localStorage.getItem(ACCESS_TOKEN_KEY);
	}

	clearTokens(): void {
		if (!this.isBrowser) return;
		localStorage.removeItem(ACCESS_TOKEN_KEY);
		// Also purge any refresh token stored by older builds.
		localStorage.removeItem(REFRESH_TOKEN_KEY);
	}

	// Only the short-lived access token is persisted. The refresh token is NOT
	// stored client-side: it was never used (no /auth/refresh flow) and keeping a
	// long-lived credential in localStorage only widened the XSS blast radius
	// (security audit SEC-01 / BUG-03). A future "stay logged in" feature should
	// use an HttpOnly cookie instead.
	private saveTokens(accessToken: string): void {
		if (!this.isBrowser) return;
		localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
	}

	login(credentials: LoginRequest): Observable<LoginData> {
		// BaseService.post() auto-unwraps response.data, so the emitted value is LoginData.
		return this.baseService
			.post<LoginData>(`${environment.apiBaseUrl}/auth/login`, credentials)
			.pipe(
				tap((data) => {
					this.saveTokens(data.accessToken);
					this.authStore.setUser(data.user);
					// Terapkan tema tenant segera setelah login (tanpa nunggu reload).
					this.theme.apply(data.user.primaryColor, data.user.accentColor);
				}),
			);
	}

	/**
	 * Change the current user's password. On success the backend rotates tokens
	 * (old refresh tokens are revoked), so we persist the new pair and reload the
	 * profile — clearing `mustChangePassword` in the store.
	 */
	changePassword(payload: ChangePasswordRequest): Observable<CurrentUser> {
		return this.baseService
			.post<ChangePasswordData>(`${environment.apiBaseUrl}/auth/change-password`, payload)
			.pipe(
				tap((data) => this.saveTokens(data.accessToken)),
				switchMap(() => this.loadCurrentUser()),
			);
	}

	/**
	 * Update the current user's own profile (self-service). Only the display name
	 * is editable — email/role/tenant are managed elsewhere. Backend returns the
	 * refreshed profile, which we push into the store so the UI (topbar, dsb.)
	 * reflects the new name immediately.
	 */
	updateProfile(payload: { fullName: string }): Observable<CurrentUser> {
		return this.baseService
			.patch<CurrentUser>(`${environment.apiBaseUrl}/auth/me`, payload)
			.pipe(
				tap((updated) => {
					// Merge, jangan replace: getMe tak membawa tenantFeatures/usageQuota/
					// permissionVersion, jadi kita pertahankan user store yang lengkap dan
					// hanya segarkan field yang bisa diubah dari profil (nama).
					const current = this.authStore.user();
					this.authStore.setUser(
						current ? { ...current, fullName: updated.fullName } : updated,
					);
				}),
			);
	}

	loadCurrentUser(): Observable<CurrentUser> {
		// BaseService.get() auto-unwraps response.data, so the emitted value is CurrentUser.
		return this.baseService.get<CurrentUser>(`${environment.apiBaseUrl}/auth/me`).pipe(
			tap((user) => {
				this.authStore.setUser(user);
				// Terapkan/segarkan tema tenant saat bootstrap & refresh profil.
				this.theme.apply(user.primaryColor, user.accentColor);
			}),
		);
	}

	logout(): void {
		this.clearTokens();
		this.authStore.clearUser();
		// Balikkan ke tema default + hapus cache agar tenant lain tak ikut terwarnai.
		this.theme.clear();
		this.router.navigate(['/auth/login']);
	}
}
